import { useMemo } from "react";
import { Marker, Source, Layer } from "@vis.gl/react-maplibre";

import MapContainer from "../fleetmap/MapContainer";
import valhallaPolylineDecode from "../../utils/valhallaPolylineDecode";

// 1. Casing Layer (Add this FIRST to your map so it sits underneath)
// const ROUTE_CASING_LAYER = {
//   id: "route-casing",
//   type: "line",
//   layout: {
//     "line-join": "round",
//     "line-cap": "round",
//   },
//   paint: {
//     // Scales dynamically: thinner at low zoom, wider when zoomed in
//     "line-width": [
//       "interpolate",
//       ["linear"],
//       ["zoom"],
//       10,
//       4, // At zoom 10, casing width is 4px
//       15,
//       12, // At zoom 15, casing width is 10px
//     ],
//     "line-color": "#2C5CB8", // Dark slate/charcoal for strong contrast
//     "line-opacity": 0.7,
//   },
// };

// // 2. Main Sleek Route Layer (Add this AFTER the casing layer)
// const ROUTE_MAIN_LAYER = {
//   id: "route-main",
//   type: "line",
//   layout: {
//     "line-join": "round",
//     "line-cap": "round",
//   },
//   paint: {
//     "line-width": [
//       "interpolate",
//       ["linear"],
//       ["zoom"],
//       10,
//       2, // At zoom 10, main line width is 2px
//       15,
//       6, // At zoom 15, main line width is 6px
//     ],
//     "line-color": "#3b82f6", // Vibrant transit blue (customize per route)
//     "line-opacity": 0.85,
//   },
// };

const ROUTE_ACCENT_LAYER = {
  id: "route-accent-centerline",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-width": 2,
    "line-color": "#1e293b", // Crisp white dashes
    "line-dasharray": [2, 10], // Creates a dotted line effect
    "line-opacity": 0.8,
  },
};

// LAYER 1: The Glow/Shadow (Creates a soft ambient halo underneath)
const ROUTE_GLOW_LAYER = {
  id: "route-glow",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: {
    "line-color": "#2563eb",
    "line-opacity": 0.3,
    "line-blur": 3, // Softens the edge for a glowing shadow effect
    "line-width": [
      "interpolate",
      ["exponential", 1.5],
      ["zoom"],
      10,
      6,
      15,
      18,
    ],
  },
};

// LAYER 2: Crisp Dark Casing (Provides sharp separation from base roads)
const ROUTE_CASING_LAYER = {
  id: "route-casing",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: {
    "line-color": "#0f172a", // Deep slate black
    "line-opacity": 0.95,
    "line-width": [
      "interpolate",
      ["exponential", 1.5],
      ["zoom"],
      10,
      4,
      15,
      12,
    ],
  },
};

// LAYER 3: Vibrant Core Line (The actual route color with an inset dash)
const ROUTE_MAIN_LAYER = {
  id: "route-main",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: {
    "line-color": "#3b82f6", // Bright transit blue
    "line-opacity": 1.0,
    "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 10, 2, 15, 7],
  },
};

const RouteMap = ({ onMapReady, onMapClick, waypoints = [], route = null }) => {
  const routeGeoJson = useMemo(() => {
    if (!route?.legs?.length) {
      return null;
    }

    const coordinates = [];

    route.legs.forEach((leg, legIndex) => {
      const legCoordinates = valhallaPolylineDecode(leg.encodedPolyline, 6);

      if (!legCoordinates.length) {
        return;
      }

      // The end point of the previous leg is normally
      // the start point of the next leg.
      // Avoid duplicating it in our combined LineString.
      if (legIndex > 0 && coordinates.length > 0) {
        legCoordinates.shift();
      }

      coordinates.push(...legCoordinates);
    });

    if (coordinates.length < 2) {
      return null;
    }

    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates,
      },
    };
  }, [route]);

  return (
    <MapContainer
      onMapReady={onMapReady}
      onMapClick={onMapClick}
      center={[-33.84024, 150.88214]}
      zoom={16}
    >
      {/* Calcuated route */}
      {routeGeoJson && (
        <Source id="route-source" type="geojson" data={routeGeoJson}>
          {/* 1. Casing layer renders underneath for contrast */}
          {/* <Layer {...ROUTE_CASING_LAYER} /> */}

          {/* 2. Main route line renders on top */}
          {/* <Layer {...ROUTE_MAIN_LAYER} /> */}

          <Layer {...ROUTE_GLOW_LAYER} />
          <Layer {...ROUTE_CASING_LAYER} />
          <Layer {...ROUTE_MAIN_LAYER} />
          {/* Optional: Add the accent/dotted centerline layer here if you want it */}
          <Layer {...ROUTE_ACCENT_LAYER} />
        </Source>
      )}

      {/* selected waypoints MARKERS */}
      {waypoints.map((waypoint, index) => {
        if (!waypoint.location?.coordinates) {
          return null;
        }

        const [lng, lat] = waypoint.location.coordinates;

        const isFirst = index === 0;
        const isLast = index === waypoints.length - 1;

        let waypointType = "stop";
        if (isFirst) {
          waypointType = "start";
        } else if (isLast) {
          waypointType = "end";
        }

        return (
          <Marker
            key={waypoint.waypointId}
            longitude={lng}
            latitude={lat}
            anchor="center"
          >
            <div
              className={`route-waypoint-marker route-waypoint-marker-${waypointType}`}
            >
              {index + 1}
            </div>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default RouteMap;
